import { DEFAULT_FEATURE_FLAGS, FEATURE_FLAG_DESCRIPTIONS } from '@/config/featureFlags';

class FeatureFlagService {
  constructor() {
    this.flags = { ...DEFAULT_FEATURE_FLAGS };
    this.loadFromStorage();
  }

  /**
   * Load feature flags from localStorage
   */
  loadFromStorage() {
    try {
      const storedFlags = localStorage.getItem('sellary_feature_flags');
      if (storedFlags) {
        const parsedFlags = JSON.parse(storedFlags);
        
        // Check if we need to migrate from old format (simple booleans)
        const needsMigration = Object.values(parsedFlags).some(flag => 
          typeof flag === 'boolean' || !Object.prototype.hasOwnProperty.call(flag, 'state')
        );
        
        if (needsMigration) {
          console.log('Migrating feature flags from old format...');
          // Clear old data and use defaults
          localStorage.removeItem('sellary_feature_flags');
          this.flags = { ...DEFAULT_FEATURE_FLAGS };
          return;
        }
        
        // Merge stored flags with defaults, preserving the structure
        Object.keys(parsedFlags).forEach(flagName => {
          if (this.flags[flagName]) {
            this.flags[flagName] = {
              ...this.flags[flagName],
              value: parsedFlags[flagName].value,
              state: parsedFlags[flagName].state
            };
          }
        });
      }
    } catch (error) {
      console.warn('Failed to load feature flags from storage:', error);
    }
  }

  /**
   * Save feature flags to localStorage
   */
  saveToStorage() {
    try {
      localStorage.setItem('sellary_feature_flags', JSON.stringify(this.flags));
    } catch (error) {
      console.warn('Failed to save feature flags to storage:', error);
    }
  }

  /**
   * Get the current value of a feature flag
   * @param {string} flagName - The name of the feature flag
   * @returns {boolean} - The current value of the flag
   */
  getFlag(flagName) {
    const flag = this.flags[flagName] || DEFAULT_FEATURE_FLAGS[flagName];
    return flag ? flag.value : false;
  }

  /**
   * Get the current state of a feature flag
   * @param {string} flagName - The name of the feature flag
   * @returns {string} - The current state of the flag ('on', 'off', 'disabled')
   */
  getFlagState(flagName) {
    const flag = this.flags[flagName] || DEFAULT_FEATURE_FLAGS[flagName];
    return flag ? flag.state : 'disabled';
  }

  /**
   * Check if a flag is changeable (not disabled)
   * @param {string} flagName - The name of the feature flag
   * @returns {boolean} - True if the flag can be changed
   */
  isFlagChangeable(flagName) {
    return this.getFlagState(flagName) !== 'disabled';
  }

  /**
   * Set the value of a feature flag
   * @param {string} flagName - The name of the feature flag
   * @param {boolean} value - The new value for the flag
   */
  setFlag(flagName, value) {
    if (Object.prototype.hasOwnProperty.call(DEFAULT_FEATURE_FLAGS, flagName)) {
      const currentState = this.getFlagState(flagName);

      // Don't allow changes to disabled flags
      if (currentState === 'disabled') {
        console.warn(`Cannot modify disabled feature flag: ${flagName}`);
        return;
      }

      this.flags[flagName] = {
        value: value,
        state: value ? 'on' : 'off'
      };
      this.saveToStorage();
    } else {
      console.warn(`Unknown feature flag: ${flagName}`);
    }
  }

  /**
   * Check if a feature flag is enabled
   * @param {string} flagName - The name of the feature flag
   * @returns {boolean} - True if the flag is enabled
   */
  isEnabled(flagName) {
    return this.getFlag(flagName) === true;
  }

  /**
   * Get all feature flags
   * @returns {Object} - All current feature flags
   */
  getAllFlags() {
    return { ...this.flags };
  }

  /**
   * Reset all flags to default values
   */
  resetToDefaults() {
    this.flags = { ...DEFAULT_FEATURE_FLAGS };
    this.saveToStorage();
  }

  /**
   * Force reset all flags and clear localStorage
   */
  forceResetToDefaults() {
    try {
      localStorage.removeItem('sellary_feature_flags');
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
    this.flags = { ...DEFAULT_FEATURE_FLAGS };
    this.saveToStorage();
  }

  /**
   * Get flags grouped by category
   * @returns {Object} - Flags grouped by category
   */
  getFlagsByCategory() {
    const categories = {};
    
    Object.keys(this.flags).forEach(flagName => {
      const description = FEATURE_FLAG_DESCRIPTIONS[flagName];
      const category = description?.category || 'Other';
      
      if (!categories[category]) {
        categories[category] = [];
      }
      
      categories[category].push({
        name: flagName,
        value: this.flags[flagName].value,
        state: this.flags[flagName].state,
        changeable: this.isFlagChangeable(flagName),
        title: description?.title || flagName,
        description: description?.description || ''
      });
    });
    
    return categories;
  }
}

// Create a singleton instance
const featureFlagService = new FeatureFlagService();

export default featureFlagService;